import { Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComponentsModule, InputOption, ResultDataSource, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { ResultObservable, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { map, shareReplay } from 'rxjs';
import { SettingsService } from '@lib/setting/settings.service';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { Router } from '@angular/router';
import {
    CareOptionSingleResponse,
    StoreCareOptionRequest,
    createCareOptionFormGroup,
    CareTypeName
} from '@lib/api/models/setting';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';

@Component({
    selector: 'app-settings-care-options-add-page',
    imports: [ComponentsModule, ReactiveFormsModule, HeaderComponent, FormsModule, ReactiveFormsModule, ErrorPanelComponent],
    templateUrl: './settings-care-options-add-page.component.html',
    standalone: true,
    styleUrl: './settings-care-options-add-page.component.scss'
})
export class SettingsCareOptionsAddPageComponent {

    private readonly _settingService = inject(SettingsService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _router = inject(Router);

    protected readonly careOptionFormGroup = createCareOptionFormGroup();

    protected readonly careTypes$: ResultObservable<InputOption<CareTypeName>[]> = this._settingService.getAllPossibleCareTypes().pipe(
        map((response): InputOption<CareTypeName>[] => {
            return (response?.data ?? '').map(careType => ({
                value: careType,
                label: careType.name
            }));
        }),
        toResult(),
        shareReplay(1)
    );

    protected searchableCareTypeDataSource = new ResultDataSource<CareTypeName>({
        source: this.careTypes$,
        searchable: true,
        compareValue: (a, b) => a.id === b.id
    });

    createCareOptionRequest = new SendableRequest<CareOptionSingleResponse, StoreCareOptionRequest>({
        request: payload => this._settingService.createCareOption(payload!),

        onSend: () => {
            this.careOptionFormGroup.disable();
        },

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'Care Option added successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            this._router.navigate(['/settings/care-options'])
                .then(success => {
                    if (!success) {
                        this._snackBar.open({
                            colour: 'error',
                            dismissable: false,
                            icon: 'error_outline',
                            title: 'Failed to navigate to care options page',
                            actions: [
                                { id: 'undo', label: 'OK' }
                            ]
                        });
                        return;
                    }
                });
        },

        onError: () => {
            this.careOptionFormGroup.enable();
        }
    });

    addCareOption(): void {
        if (this.createCareOptionRequest.loading()) {
            return;
        }

        if (!this.careOptionFormGroup.valid) {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error',
                title: 'Please fill in all required fields',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            return;
        }

        const formValues = this.careOptionFormGroup.value;
        const careTypeValue = formValues.care_type_id;

        const careTypeId = typeof careTypeValue === 'object' && careTypeValue
            ? (careTypeValue as CareTypeName).id
            : (careTypeValue as string) || '';

        const careTypeData: StoreCareOptionRequest = {
            name: formValues.name ?? '',
            description: formValues.description ?? '',
            include_price: formValues.include_price ?? false,
            care_type_id: careTypeId
        };

        this.createCareOptionRequest.send(careTypeData);
    }

    cancel(): void {
        this._router.navigate(['/settings/care-options'])
            .then(success => {
                if (!success) {
                    this._snackBar.open({
                        colour: 'error',
                        icon: 'error',
                        title: 'Failed to navigate to the care options index page.'
                    });
                    return;
                }
            });
    }
}
